import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const SuperAdminBenefit = () => {
  const [jobSeekers, setJobSeekers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedJobSeeker, setSelectedJobSeeker] = useState(null);

  // Fetch benefits and group by Job Seeker
  useEffect(() => {
    const fetchJobSeekers = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}benefit/getAllUserBenefit`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });

        const benefits = response.data.data || [];
        const jobSeekerMap = new Map();

        for (const benefit of benefits) {
          const js = benefit.applicantId;
          if (!js) continue;

          if (!jobSeekerMap.has(js._id)) {
            jobSeekerMap.set(js._id, { 
              ...js, 
              benefits: [], 
              salaryHistory: js.hiredHistory?.[0]?.salary?.history || [] 
            });
          }
          jobSeekerMap.get(js._id).benefits.push(benefit);
        }

        setJobSeekers(Array.from(jobSeekerMap.values()));
      } catch (err) {
        setError(err.message || "Failed to fetch benefits");
        toast.error("Error fetching benefits");
      } finally {
        setLoading(false);
      }
    };

    fetchJobSeekers();
  }, []);

  const updateBenefitStatus = async (benefitId, status, amountRequested) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}benefit/update`,
        { benefitId, benefitStatus: status, benefitBalance: status === "Approved" ? amountRequested : "0" },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );

      // Update state
      setJobSeekers((prev) =>
        prev.map((js) => ({
          ...js,
          benefits: js.benefits.map((b) =>
            b._id === benefitId ? { ...b, ...response.data.data } : b
          ),
        }))
      );

      toast.success(`Benefit ${status.toLowerCase()} successfully`);
    } catch (err) {
      toast.error("Failed to update benefit");
    }
  };

  const getTotalBenefits = (benefits) => {
    const totals = { essential: 0, health: 0, all: 0 };
    benefits?.forEach((b) => {
      if (b.benefitStatus === "Approved") {
        const amount = parseFloat(b.amountRequested || 0);
        totals[b.benefitType] = (totals[b.benefitType] || 0) + amount;
        totals.all += amount;
      }
    });
    return totals;
  };

  const toggleDetails = (jobSeeker) => {
    setSelectedJobSeeker(selectedJobSeeker?._id === jobSeeker._id ? null : jobSeeker);
  };

  if (loading) return <div className="text-center py-10">Loading benefits...</div>;
  if (error) return <div className="text-red-500 text-center py-10">{error}</div>;

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center md:text-left">
        Job Seeker Benefits Overview
      </h2>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-4 px-6 text-left">Name</th>
              <th className="py-4 px-6 text-left">Total Essential</th>
              <th className="py-4 px-6 text-left">Total Health</th>
              <th className="py-4 px-6 text-left">Total Benefits</th>
              <th className="py-4 px-6 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobSeekers.map((js) => {
              const totals = getTotalBenefits(js.benefits);
              return (
                <React.Fragment key={js._id}>
                  <tr className="border-t hover:bg-gray-50">
                    <td className="py-4 px-6 font-medium">
                      {js.firstName} {js.lastName}
                    </td>
                    <td className="py-4 px-6">₦{totals.essential.toLocaleString()}</td>
                    <td className="py-4 px-6">₦{totals.health.toLocaleString()}</td>
                    <td className="py-4 px-6 font-semibold">₦{totals.all.toLocaleString()}</td>
                    <td className="py-4 px-6 text-center">
                      <button
                        onClick={() => toggleDetails(js)}
                        className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                      >
                        {selectedJobSeeker?._id === js._id ? "Hide" : "View Details"}
                      </button>
                    </td>
                  </tr>

                  {selectedJobSeeker?._id === js._id && (
                    <tr>
                      <td colSpan="5" className="bg-gray-50 p-6">
                        {/* Details content remains same */}
                        <BenefitDetails 
                          jobSeeker={js} 
                          updateBenefitStatus={updateBenefitStatus} 
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-6">
        {jobSeekers.map((js) => {
          const totals = getTotalBenefits(js.benefits);
          const isOpen = selectedJobSeeker?._id === js._id;

          return (
            <div key={js._id} className="bg-white rounded-2xl shadow p-5">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-lg">
                    {js.firstName} {js.lastName}
                  </h3>
                  <p className="text-sm text-gray-500">{js.email}</p>
                </div>
                <button
                  onClick={() => toggleDetails(js)}
                  className="text-blue-600 font-medium text-sm"
                >
                  {isOpen ? "Hide" : "View"}
                </button>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xs text-gray-500">Essential</p>
                  <p className="font-semibold">₦{totals.essential.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Health</p>
                  <p className="font-semibold">₦{totals.health.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="font-bold text-green-600">₦{totals.all.toLocaleString()}</p>
                </div>
              </div>

              {isOpen && (
                <div className="mt-6">
                  <BenefitDetails 
                    jobSeeker={js} 
                    updateBenefitStatus={updateBenefitStatus} 
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Extracted Details Component for better readability
const BenefitDetails = ({ jobSeeker, updateBenefitStatus }) => {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-semibold mb-2">Personal Info</h4>
        <p><strong>Email:</strong> {jobSeeker.email}</p>
        <p><strong>Phone:</strong> {jobSeeker.phoneNumber}</p>
      </div>

      {/* Benefit Requests */}
      <div>
        <h4 className="font-semibold mb-3">Benefit Requests</h4>
        <div className="space-y-4">
          {jobSeeker.benefits?.map((benefit) => (
            <div key={benefit._id} className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between">
                <div>
                  <p className="capitalize font-medium">{benefit.benefitType}</p>
                  <p className="text-sm text-gray-600">{benefit.benefitDescription}</p>
                </div>
                <p className="font-semibold">₦{parseFloat(benefit.amountRequested).toLocaleString()}</p>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                  benefit.benefitStatus === "Approved" ? "bg-green-100 text-green-700" :
                  benefit.benefitStatus === "rejected" ? "bg-red-100 text-red-700" :
                  "bg-yellow-100 text-yellow-700"
                }`}>
                  {benefit.benefitStatus}
                </span>

                {benefit.benefitStatus === "Pending" && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateBenefitStatus(benefit._id, "Approved", benefit.amountRequested)}
                      className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-lg"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => updateBenefitStatus(benefit._id, "rejected", "0")}
                      className="px-4 py-1.5 bg-red-600 text-white text-sm rounded-lg"
                    >
                      Decline
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminBenefit;